'use client';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import IncomingCallModal from './IncomingCallModal';
import VideoCallModal from './VideoCallModal';

interface IncomingCallData {
  callerName: string;
  callerId: string;
  chatId: string;
  callType: 'audio' | 'video';
  offer: RTCSessionDescriptionInit;
}

export default function CallNotificationHandler() {
  const { user } = useAuth();
  const { subscribe, sendCallReject, sendCallLog } = useWebSocket();
  const [incomingCall, setIncomingCall] = useState<IncomingCallData | null>(null);
  const [activeCall, setActiveCall] = useState<{
    type: 'audio' | 'video';
    contactName: string;
    contactId: string;
    chatId: string;
    isIncoming: boolean;
    offer?: RTCSessionDescriptionInit;
  } | null>(null);

  const ringtoneRef = useRef<any>(null);
  const ringtoneTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const incomingCallRef = useRef<IncomingCallData | null>(null);

  useEffect(() => { incomingCallRef.current = incomingCall; }, [incomingCall]);

  useEffect(() => {
    if (!user) return;
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [user]);

  const playRingtone = useCallback(() => {
    try {
      if (ringtoneRef.current) return;
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.type = 'sine';
      oscillator.frequency.value = 440;
      gainNode.gain.value = 0;
      const now = audioContext.currentTime;
      for (let i = 0; i < 20; i++) {
        const t = now + i * 2;
        gainNode.gain.setValueAtTime(0.25, t);
        gainNode.gain.setValueAtTime(0.25, t + 0.4);
        gainNode.gain.setValueAtTime(0, t + 0.5);
        gainNode.gain.setValueAtTime(0.25, t + 0.7);
        gainNode.gain.setValueAtTime(0.25, t + 1.1);
        gainNode.gain.setValueAtTime(0, t + 1.2);
      }
      oscillator.start();
      ringtoneRef.current = { oscillator, audioContext, gainNode };
    } catch (e) {
      console.warn('Could not play ringtone:', e);
    }
  }, []);

  const stopRingtone = useCallback(() => {
    try {
      const ref = ringtoneRef.current;
      if (ref?.oscillator) { ref.oscillator.stop(); ref.audioContext.close(); }
    } catch { /* ignore */ }
    ringtoneRef.current = null;
    if (ringtoneTimeoutRef.current) { clearTimeout(ringtoneTimeoutRef.current); ringtoneTimeoutRef.current = null; }
  }, []);

  const showBrowserNotification = useCallback((callerName: string, callType: 'audio' | 'video') => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    try {
      const notification = new Notification(`📞 Incoming ${callType} call`, {
        body: `${callerName} is calling you`,
        icon: '/favicon.ico',
        tag: 'incoming-call',
        requireInteraction: true,
        silent: false,
      } as NotificationOptions);
      notification.onclick = () => { window.focus(); notification.close(); };
      setTimeout(() => notification.close(), 30000);
    } catch { /* ignore */ }
  }, []);

  const handleSWAccept = useCallback(() => {
    const call = incomingCallRef.current;
    if (!call) return;
    stopRingtone();
    setActiveCall({ type: call.callType, contactName: call.callerName, contactId: call.callerId, chatId: call.chatId, isIncoming: true, offer: call.offer });
    setIncomingCall(null);
  }, [stopRingtone]);

  const handleSWReject = useCallback(() => {
    const call = incomingCallRef.current;
    if (!call) return;
    stopRingtone();
    sendCallReject(call.callerId, call.chatId);
    sendCallLog(call.chatId, call.callType, 'rejected');
    setIncomingCall(null);
  }, [stopRingtone, sendCallReject, sendCallLog]);

  usePushNotifications(handleSWAccept, handleSWReject);

  useEffect(() => {
    if (!user) return;
    const unsubOffer = subscribe('call_offer', (data: any) => {
      const callData: IncomingCallData = { callerName: data.callerName || 'Unknown', callerId: data.callerId, chatId: data.chatId, callType: data.callType || 'audio', offer: data.offer };
      setIncomingCall(callData);
      playRingtone();
      showBrowserNotification(callData.callerName, callData.callType);
      ringtoneTimeoutRef.current = setTimeout(() => {
        setIncomingCall(null);
        stopRingtone();
        sendCallReject(callData.callerId, callData.chatId);
        sendCallLog(callData.chatId, callData.callType, 'missed');
      }, 30000);
    });
    const unsubEnded = subscribe('call_ended', () => { setIncomingCall(null); stopRingtone(); });
    const unsubRejected = subscribe('call_rejected', () => { setIncomingCall(null); stopRingtone(); });
    return () => { unsubOffer(); unsubEnded(); unsubRejected(); stopRingtone(); };
  }, [user, subscribe, playRingtone, stopRingtone, showBrowserNotification, sendCallReject, sendCallLog]);

  const handleAccept = useCallback(() => {
    if (!incomingCall) return;
    stopRingtone();
    setActiveCall({ type: incomingCall.callType, contactName: incomingCall.callerName, contactId: incomingCall.callerId, chatId: incomingCall.chatId, isIncoming: true, offer: incomingCall.offer });
    setIncomingCall(null);
  }, [incomingCall, stopRingtone]);

  const handleReject = useCallback(() => {
    if (!incomingCall) return;
    stopRingtone();
    sendCallReject(incomingCall.callerId, incomingCall.chatId);
    sendCallLog(incomingCall.chatId, incomingCall.callType, 'rejected');
    setIncomingCall(null);
  }, [incomingCall, stopRingtone, sendCallReject, sendCallLog]);

  const handleCallClose = useCallback(() => { setActiveCall(null); }, []);

  if (!user) return null;

  return (
    <>
      {incomingCall && (
        <IncomingCallModal isOpen={true} callerName={incomingCall.callerName} callType={incomingCall.callType} onAccept={handleAccept} onReject={handleReject} />
      )}
      {activeCall && (
        <VideoCallModal isOpen={true} onClose={handleCallClose} callType={activeCall.type} contactName={activeCall.contactName} contactId={activeCall.contactId} chatId={activeCall.chatId} isIncoming={activeCall.isIncoming} offer={activeCall.offer} />
      )}
    </>
  );
}
